import _ from 'lodash';
import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import { queryApiData } from '@/services/global';
import { selectCommercialBillingApiRoute } from '@/components/SiderMenu/utils/sessionNavigation';

import { normalizeRegistrationOptions } from './actorKindOptions';
import { MODEL_NAME, NAVIGATION_KEY } from './constants';
import { isNonOperationalRegistration, mapOnboardingValidationErrorsForForm } from './utils';

const api = bindServices(MODEL_NAME);

const namespace = MODEL_NAME;
const navigationKey = NAVIGATION_KEY;

const getUnknownErrorMessage = () => getIntl().formatMessage({ id: 'global.error.unknown' });

const unwrapParentOptionsResponse = (data) => {
  if (Array.isArray(data?.options)) return data.options;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  return [];
};

const initialState = {
  submitting: false,
  registerResult: null,
  validationErrors: [],
  registrationOptions: [],
  onboardingSummary: null,
  onboardingSummaryLoading: false,
  onboardingSummaryError: null,
  contextRevision: 0,
};

function* loadOnboardingSummary(companyId, { call, put, select }) {
  if (!companyId) {
    yield put({ type: 'setOnboardingSummaryLoading', payload: false });
    return;
  }

  const baseRoute = yield* selectCommercialBillingApiRoute(navigationKey, select);
  if (!baseRoute) {
    yield put({ type: 'setOnboardingSummaryLoading', payload: false });
    yield put({
      type: 'setOnboardingSummaryError',
      payload: getUnknownErrorMessage(),
    });
    return;
  }

  yield put({ type: 'setOnboardingSummaryLoading', payload: true });

  try {
    const response = yield call(
      api.getById,
      {
        route: baseRoute,
        id: `${companyId}/summary`,
      },
      { suppressErrorNotification: true },
    );
    yield put({ type: 'setOnboardingSummaryError', payload: null });
    yield put({
      type: 'setOnboardingSummary',
      payload: response,
    });
  } catch (e) {
    yield put({
      type: 'setOnboardingSummaryError',
      payload: e?.message || getUnknownErrorMessage(),
    });
    yield put({
      type: 'setOnboardingSummary',
      payload: null,
    });
  } finally {
    yield put({ type: 'setOnboardingSummaryLoading', payload: false });
  }
}

const ChannelOnboardingModel = {
  namespace,
  state: initialState,

  effects: {
    *fetchRegistrationOptions(_, { call, put, select }) {
      try {
        const baseRoute = yield* selectCommercialBillingApiRoute(navigationKey, select);
        if (!baseRoute) return;
        const response = yield call(api.getAll, {
          route: `${baseRoute}/registration-options`,
        });
        yield put({
          type: 'setRegistrationOptions',
          payload: normalizeRegistrationOptions(response),
        });
      } catch (e) {
        yield put({
          type: 'setRegistrationOptions',
          payload: normalizeRegistrationOptions(null),
        });
      }
    },

    *fetchParentOptions({ payload }, { call, put }) {
      try {
        yield put({
          type: 'global/queryApiData',
          payload: { ...payload, loading: true },
        });
        const response = yield call(queryApiData, payload);
        yield put({
          type: 'global/queryApiData',
          payload: {
            ...payload,
            data: unwrapParentOptionsResponse(response),
            loading: false,
          },
        });
      } catch (e) {
        const isAbort =
          e?.code === 'ERR_CANCELED' ||
          e?.name === 'CanceledError' ||
          e?.name === 'AbortError' ||
          e?.aborted === true;
        yield put({
          type: 'global/queryApiData',
          payload: { ...payload, loading: false, ...(isAbort && { aborted: true }) },
        });
      }
    },

    *register({ payload }, { call, put, select }) {
      try {
        const baseRoute = yield* selectCommercialBillingApiRoute(navigationKey, select);
        if (!baseRoute) return;
        yield put({ type: 'setSubmitting', payload: true });
        yield put({ type: 'setValidationErrors', payload: [] });
        const response = yield call(api.create, {
          route: `${baseRoute}/register`,
          data: payload,
        });
        const skipOperationalSummary = isNonOperationalRegistration(response);
        if (!skipOperationalSummary) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
        yield put({
          type: 'registerSuccess',
          payload: response,
        });
        if (!skipOperationalSummary) {
          yield* loadOnboardingSummary(response?.companyId, { call, put, select });
        }
      } catch (e) {
        const validationErrors = mapOnboardingValidationErrorsForForm(e?.validationErrors || []);
        const hasFieldLevelErrors = validationErrors.some((item) => !_.isEmpty(item?.field));

        if (e?.message || validationErrors.length) {
          if (validationErrors.length && _.isEmpty(validationErrors[0]?.field)) {
            _.set(e, 'message', validationErrors[0].errorMessages[0]);
          }

          if (hasFieldLevelErrors) {
            yield put({ type: 'global/hideNotificationForm' });
          } else if (e?.message) {
            yield put({
              type: 'global/showNotificationsForm',
              payload: { visible: true, type: 'error', message: e.message },
            });
          }

          yield put({
            type: 'setValidationErrors',
            payload: validationErrors,
          });
        }
      } finally {
        yield put({ type: 'setSubmitting', payload: false });
      }
    },

    *fetchOnboardingSummary({ payload }, saga) {
      yield* loadOnboardingSummary(payload?.companyId, saga);
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *invalidateOnboardingCache(_, { put }) {
      // NOTE: the effect must NOT share its name with the reducer below.
      // In DVA a dispatched type runs the matching reducer AND re-triggers the
      // matching effect; an effect that puts its own name would recurse forever
      // (freezing the app and flooding the API). Keep these names distinct.
      yield put({ type: 'invalidateCachedOnboardingData' });
      yield put({ type: 'fetchRegistrationOptions' });
    },
  },

  reducers: {
    setSubmitting(state, { payload }) {
      return { ...state, submitting: payload };
    },

    setValidationErrors(state, { payload }) {
      return { ...state, validationErrors: payload || [] };
    },

    setRegistrationOptions(state, { payload }) {
      return { ...state, registrationOptions: payload || [] };
    },

    setOnboardingSummary(state, { payload }) {
      return { ...state, onboardingSummary: payload || null };
    },

    setOnboardingSummaryLoading(state, { payload }) {
      return { ...state, onboardingSummaryLoading: payload };
    },

    setOnboardingSummaryError(state, { payload }) {
      return { ...state, onboardingSummaryError: payload || null };
    },

    registerSuccess(state, { payload }) {
      const skipOperationalSummary = isNonOperationalRegistration(payload);
      return {
        ...state,
        registerResult: payload,
        validationErrors: [],
        onboardingSummary: null,
        onboardingSummaryError: null,
        onboardingSummaryLoading: Boolean(payload?.companyId) && !skipOperationalSummary,
      };
    },

    clearRegistrationFlow(state) {
      return {
        ...state,
        submitting: false,
        registerResult: null,
        validationErrors: [],
        onboardingSummary: null,
        onboardingSummaryLoading: false,
        onboardingSummaryError: null,
      };
    },

    invalidateCachedOnboardingData(state) {
      return {
        ...state,
        submitting: false,
        registerResult: null,
        validationErrors: [],
        registrationOptions: [],
        onboardingSummary: null,
        onboardingSummaryLoading: false,
        onboardingSummaryError: null,
        contextRevision: (state.contextRevision || 0) + 1,
      };
    },

    reset() {
      return { ...initialState };
    },
  },
};

export default ChannelOnboardingModel;
